
import { useState } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import DateTimePicker from "@/components/calendar/DateTimePicker";
import { format } from "date-fns";

interface FocusTimeModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSchedule?: (data: FocusTimeData) => void;
}

export interface FocusTimeData {
  title: string;
  startTime: Date;
  durationMinutes: number;
  repeatWeekly: boolean;
}

const FocusTimeModal = ({ isOpen, onOpenChange, onSchedule }: FocusTimeModalProps) => {
  const { toast } = useToast();
  const [title, setTitle] = useState("Focus Time");
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [time, setTime] = useState("09:00");
  const [duration, setDuration] = useState("60");
  const [repeatWeekly, setRepeatWeekly] = useState(false);

  const getStartTime = () => {
    if (!date) return new Date();
    
    const [hours, minutes] = time.split(':').map(Number);
    const startDate = new Date(date);
    startDate.setHours(hours, minutes, 0, 0);
    
    return startDate;
  };

  const handleSubmit = () => {
    if (!date) {
      toast({
        title: "Missing information",
        description: "Please select a date and time",
        variant: "destructive"
      });
      return;
    }

    const data: FocusTimeData = {
      title,
      startTime: getStartTime(),
      durationMinutes: parseInt(duration),
      repeatWeekly
    };

    if (onSchedule) {
      onSchedule(data);
    }
    
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Schedule Focus Time</DialogTitle>
          <DialogDescription>
            Block time on your calendar for deep work.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="title" className="text-right">
              Title
            </Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="col-span-3"
            />
          </div>
          
          <DateTimePicker
            date={date}
            time={time}
            onDateChange={setDate}
            onTimeChange={setTime}
          />
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="duration" className="text-right">
              Duration
            </Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="repeat" className="text-right">
              Repeat Weekly
            </Label>
            <div className="col-span-3 flex items-center space-x-2">
              <Switch
                id="repeat"
                checked={repeatWeekly}
                onCheckedChange={setRepeatWeekly}
              />
              <Label htmlFor="repeat" className="text-sm">
                {repeatWeekly ? "Yes" : "No"}
              </Label>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button type="submit" onClick={handleSubmit}>Schedule Focus Time</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FocusTimeModal;
